import { useEffect, useState } from 'react'
import Head from 'next/head'
import dynamic from 'next/dynamic'
import Axios from 'axios'
import getConfig from 'next/config'
import Cookies from 'universal-cookie'
import { useRouter } from 'next/router'
import PdpaUtility from '../utility/pdpaUtility'
import {
  renderTrackingAnalyticsScriptsHead,
  TrackingAnalyticsScriptsBody,
} from '../common/TrackingAnalyticsScripts'
import HeaderNew from '../home-new/HeaderNew'
import FooterNew from '../home-new/FooterNew'
import T from '../home-new/tokens'
import { canonicalUrl, alternateUrls } from '../../utils/canonical'

// Lean new-design shell for the inner pages' `?new` variants.
//
// It does NOT route through the shared MUI `Layout` (components/common/
// Layout.js) — Layout put the whole MUI runtime + its widgets on the critical
// path. Instead this shell renders its own minimal <Head> (SEO/OG meta
// mirrored verbatim from Layout) plus the static-CSS HeaderNew / FooterNew
// chrome.
//
// EVERYTHING Layout provided is still here — nothing is dropped — but the
// non-critical parts are DEFERRED rather than blocking:
//   • Analytics: the PDPA-gated tracking scripts (FB Pixel, GA, GTM-NLGJRSV,
//     LINE, Hotjar) render in <Head>/<body> exactly as Layout did. The global
//     GTM-54FG243 in pages/_document.js is untouched and additional to these.
//   • PDPA consent popup: same state machine + same props as Layout, mounted
//     client-side (ssr:false) as soon as the consent state resolves, so the
//     legal/compliance behaviour matches the old pages.
//   • SSO auto-login re-check: the Layout `check_sso_login` cookie gate,
//     copied verbatim (including the /checkprice exclusion and the
//     `shippop_visited` gate).
//   • MascotBox + FB Messenger: same components, but mounted only AFTER the
//     browser goes idle (requestIdleCallback, 3s timeout fallback), so they
//     cost nothing on the critical path. Previously Messenger's SDK loaded
//     eagerly with the page.
//
// HeaderNew is position:fixed, so `main` keeps the top padding to clear it.
const { publicRuntimeConfig } = getConfig()

const PdpaPopup = dynamic(() => import('../common/PdpaPopup/PdpaPopup'), {
  ssr: false,
})
const MascotBox = dynamic(() => import('../common/MascotBox/MascotBox'), {
  ssr: false,
})
const Messenger = dynamic(() => import('../common/Messenger'), { ssr: false })

const NewShell = ({ title, description, keywords, children, headExtra }) => {
  const Router = useRouter()
  const cookies = new Cookies()

  const [isPdpaShowing, setPdpaShowing] = useState(false)
  const [editCookiesMode, setEditCookiesMode] = useState(false)
  // Gates the deferred floating widgets (MascotBox / Messenger) until idle.
  const [idleReady, setIdleReady] = useState(false)
  const [pdpaStates, setPdpaStates] = useState({
    isSetPdpa: false,
    allowTargeting: false,
    allowFunctionality: false,
    allowPerformance: false,
  })

  const setPdpaStateFromUtility = (
    { targeting, functionality, performance },
    isNotSet = false
  ) => {
    if (isNotSet) return
    setPdpaStates({
      isSetPdpa: true,
      allowTargeting: targeting,
      allowFunctionality: functionality,
      allowPerformance: performance,
    })
  }

  const setCheckSsoLoginCookie = () => {
    cookies.set('check_sso_login', 1, { maxAge: 2 * 60, path: '/' })
  }

  const redirectToLogin = () => {
    const currentUrl = encodeURIComponent(
      `${publicRuntimeConfig.siteDomain}${Router.asPath}`
    )
    window.location.replace(
      `${publicRuntimeConfig.memberService}/sso/login?cbUrl=${
        encodeURIComponent(
          `${publicRuntimeConfig.siteDomain}/verify?destUrl=`
        ) + currentUrl
      }&homePageUrl=${currentUrl}`
    )
  }

  // Verbatim copy of Layout.js's mount effect (SSO re-check + PDPA resolution
  // + shippop_visited cookie), so ?new pages behave identically to old pages.
  useEffect(() => {
    const checkIsLoggedInInOtherServices = () => {
      setCheckSsoLoginCookie()
      redirectToLogin()
    }
    const checkIsLoggedOutOrChangeAccountInOtherServices = async () => {
      setCheckSsoLoginCookie()
      try {
        if (localStorage.getItem('jwt_token')) {
          // check is logged in
          const res = await Axios.post('/api/auth/is-sso-logged-in', {
            jwtToken: localStorage.getItem('jwt_token'),
          })
          const status = res.data.status
          if (status !== 'success') {
            localStorage.removeItem('shp_user')
            localStorage.removeItem('jwt_token')
            redirectToLogin()
          }
        }
      } catch (err) {
        // do nothing
      }
    }

    // ******* ========== Load page flow (about member) =========== ************
    if (
      !cookies.get('check_sso_login') &&
      !Router.asPath.includes('/checkprice') &&
      cookies.get('shippop_visited') === 'true'
    ) {
      // if it is the time to recheck login status
      // exclude /checkprice as it required to be redirected to from index when use checkprice form
      if (localStorage.getItem('jwt_token'))
        checkIsLoggedOutOrChangeAccountInOtherServices()
      else checkIsLoggedInInOtherServices()
    } else {
      // if not have to check login status
      const pdpaLocalStorage = PdpaUtility.getPdpaLocalStorage()
      const pdpaCookiesLogin = PdpaUtility.getPdpaCookies(true)
      const pdpaCookiesNoneLogin = PdpaUtility.getPdpaCookies(false)
      // case already login
      if (localStorage.getItem('jwt_token')) {
        if (pdpaLocalStorage) {
          PdpaUtility.setPdpaCookies(true, pdpaLocalStorage)
          setPdpaStateFromUtility(pdpaLocalStorage)
        } else {
          if (pdpaCookiesLogin) {
            PdpaUtility.setPdpaLocalStorage(pdpaCookiesLogin)
            setPdpaStateFromUtility(pdpaCookiesLogin)
            PdpaUtility.updatePdpaMemberService(pdpaCookiesLogin)
          } else {
            if (pdpaCookiesNoneLogin) {
              PdpaUtility.setPdpaLocalStorage(pdpaCookiesNoneLogin)
              PdpaUtility.setPdpaCookies(true, pdpaCookiesNoneLogin)
              setPdpaStateFromUtility(pdpaCookiesNoneLogin)
              PdpaUtility.updatePdpaMemberService(pdpaCookiesNoneLogin)
            } else {
              setPdpaStateFromUtility({}, true)
            }
          }
        }
      }
      // case not log in yet
      else {
        if (pdpaCookiesNoneLogin) {
          setPdpaStateFromUtility(pdpaCookiesNoneLogin)
        } else {
          setPdpaStateFromUtility({}, true)
        }
      }
    }

    // show pdpa popup if meet criteria
    if (
      !PdpaUtility.getPdpaByPriority().isSet &&
      publicRuntimeConfig.enablePdpa === 'true'
    ) {
      setPdpaShowing(true)
    }

    // set cookies that tell this browser have visited shippop before
    cookies.set('shippop_visited', 'true', { path: '/' })
  }, [])

  // Mount the floating widgets (MascotBox / Messenger) only once the browser
  // is idle — they are support affordances, not first-paint content.
  useEffect(() => {
    let handle
    const ric =
      typeof window !== 'undefined' && window.requestIdleCallback
        ? window.requestIdleCallback
        : (cb) => setTimeout(cb, 3000)
    const cancel =
      typeof window !== 'undefined' && window.cancelIdleCallback
        ? window.cancelIdleCallback
        : clearTimeout
    handle = ric(() => setIdleReady(true), { timeout: 3000 })
    return () => cancel(handle)
  }, [])

  const setPdpa = ({ targeting, functionality, performance }) => {
    // set pdpa by save setting
    setPdpaStates({
      ...pdpaStates,
      allowTargeting: targeting,
      allowFunctionality: functionality,
      allowPerformance: performance,
    })

    if (localStorage.getItem('jwt_token')) {
      PdpaUtility.setPdpaLocalStorage({ targeting, functionality, performance })
      PdpaUtility.setPdpaCookies(true, {
        targeting,
        functionality,
        performance,
      })
      PdpaUtility.updatePdpaMemberService({
        targeting,
        functionality,
        performance,
      })
    } else {
      PdpaUtility.setPdpaCookies(false, {
        targeting,
        functionality,
        performance,
      })
    }
  }

  return (
    <>
      <Head>
        <title>{title}</title>
        <link rel="icon" href="/favicon.ico" />
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta charSet="UTF-8" />
        {/* meta for Facebook thumbnails — mirrored verbatim from Layout so the
            emitted OG/Twitter tags stay byte-identical to the previous render. */}
        <meta
          key="og:title"
          property="og:title"
          content="SHIPPOP ส่งของ ส่งพัสดุ เปรียบเทียบราคา จองขนส่ง ทำได้เลยทันที"
        />
        <meta
          key="og:description"
          property="og:description"
          content="เปรียบเทียบราคาการส่งของ ส่งพัสดุ จองขนส่งและชำระค่าบริการออนไลน์ได้ทันที ที่สำคัญไม่ต้องออกจากบ้านเพราะเดียวขนส่งมารับถึงหน้าบ้านคุณ"
        />
        <meta
          key="og:image"
          property="og:image"
          content="/static/img/home/shippop_banner.jpg"
        />
        <meta key="og:url" property="og:url" content={canonicalUrl(Router)} />
        <meta key="og:site_name" property="og:site_name" content="SHIPPOP" />
        <meta key="og:locale" property="og:locale" content="th_TH" />
        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta key="twitter:title" name="twitter:title" content={title} />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={description}
        />

        {/* Warm up the LINE add-friend widget origins used by FooterNew /
            HelpBanner on every ?new page (Lighthouse uses-rel-preconnect). */}
        <link rel="preconnect" href="https://d.line-cdn.net" crossOrigin="anonymous" />
        <link rel="dns-prefetch" href="https://d.line-cdn.net" />
        <link rel="preconnect" href="https://tr.line.me" crossOrigin="anonymous" />
        <link rel="dns-prefetch" href="https://tr.line.me" />

        <link rel="canonical" key="canonical" href={canonicalUrl(Router)} />
        <link rel="alternate" hrefLang="th" href={alternateUrls(Router).th} />
        <link rel="alternate" hrefLang="en" href={alternateUrls(Router).en} />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={alternateUrls(Router).th}
        />

        {/* Per-page head extras (e.g. LCP-image preload) */}
        {headExtra}

        <link
          rel="preload"
          href="/static/fonts/sukhumvitset/SukhumvitSet-Text.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href="/static/fonts/sukhumvitset/SukhumvitSet-Medium.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <style
          dangerouslySetInnerHTML={{
            __html: `
@font-face{font-family:'Sukhumvit Set';src:url('/static/fonts/sukhumvitset/SukhumvitSet-Text.woff2') format('woff2');font-weight:400;font-style:normal;font-display:swap;}
@font-face{font-family:'Sukhumvit Set';src:url('/static/fonts/sukhumvitset/SukhumvitSet-Medium.woff2') format('woff2');font-weight:500;font-style:normal;font-display:swap;}
@font-face{font-family:'Sukhumvit Set';src:url('/static/fonts/sukhumvitset/SukhumvitSet-SemiBold.woff2') format('woff2');font-weight:600;font-style:normal;font-display:swap;}
@font-face{font-family:'Sukhumvit Set';src:url('/static/fonts/sukhumvitset/SukhumvitSet-Bold.woff2') format('woff2');font-weight:700;font-style:normal;font-display:swap;}
`,
          }}
        />
        {renderTrackingAnalyticsScriptsHead(pdpaStates)}
      </Head>
      <TrackingAnalyticsScriptsBody pdpaStates={pdpaStates} />

      <div
        style={{
          fontFamily: T.font.body,
          background: T.color.canvas,
          color: T.color.ink,
          minHeight: '100vh',
        }}
      >
        <HeaderNew />
        <main style={{ paddingTop: 92 }}>{children}</main>
        <FooterNew />
      </div>

      <PdpaPopup
        isShowing={isPdpaShowing}
        setShowing={setPdpaShowing}
        pdpaStates={pdpaStates}
        setPdpa={setPdpa}
        isLoggedIn={
          typeof window !== 'undefined'
            ? localStorage.getItem('jwt_token')
            : false
        }
        editCookiesMode={editCookiesMode}
        setEditCookiesMode={setEditCookiesMode}
      />

      {/* Deferred until the browser is idle — support widgets, not content. */}
      {idleReady && (
        <>
          <Messenger />
          <MascotBox />
        </>
      )}
    </>
  )
}

export default NewShell
